import React from "react";
import { Panel } from "primereact/panel";
import moment from "moment/moment";

interface QuestionField {
  uuid: string;
  title: string;
}

interface FileUploadAnswer {
  id: string;
  name: string;
  url: string;
  mimeType: string;
  size: number;
}

interface Question {
  id: string;
  title: string;
  type: string;
  fields: QuestionField[];
}

interface Response {
  id: string;
  questionId: string;
  answer: string | string[] | FileUploadAnswer[];
}

interface TallyData {
  questions: Question[];
  responses: Response[];
}

interface ImportData {
  form_id: string;
  import_data: string;
  imported_at: string;
}

const ImportDataViewer = ({ importData }: { importData: ImportData[] }) => {
  if (!Array.isArray(importData) || importData.length === 0) {
    return <div>No import data available.</div>;
  }

  return (
    <div className="flex flex-column gap-3">
      {importData.map((item, index) => {
        let tallyData: TallyData | null = null;
        try {
          tallyData = JSON.parse(item.import_data) as TallyData;
        } catch (err) {
          console.error(`Invalid JSON for form ${item.form_id}`, err);
        }

        if (!tallyData) {
          return (
            <Panel header={`Form ID: ${item.form_id}`} toggleable key={index}>
              <div>Invalid or empty import data</div>
            </Panel>
          );
        }

        const questionMap: Record<string, Question> = {};
        tallyData.questions.forEach((q) => {
          questionMap[q.id] = q;
        });

        return (
          <Panel
            header={`Form ID: ${item.form_id} - Imported at ${moment(item.imported_at).format("YYYY-MM-DD")}`}
            toggleable
            key={index}
          >
            {tallyData.responses.map((resp) => {
              const question = questionMap[resp.questionId];
              if (!question) {
                return (
                  <p key={resp.id}>
                    <strong>Unknown Question {resp.questionId}:</strong>{" "}
                    {JSON.stringify(resp.answer)}
                  </p>
                );
              }

              const { title, type } = question;

              let answerDisplay: React.ReactNode;

              if (type === "FILE_UPLOAD" && Array.isArray(resp.answer)) {
                const fileAnswers = resp.answer as FileUploadAnswer[];
                answerDisplay = fileAnswers.map((file, idx) => (
                  <div key={file.id || idx}>
                    <a href={file.url} target="_blank" rel="noreferrer">
                      {file.name || file.url}
                    </a>{" "}
                    ({file.mimeType})
                  </div>
                ));
              } else {
                if (Array.isArray(resp.answer)) {
                  answerDisplay = resp.answer.join(", ");
                } else {
                  answerDisplay = resp.answer;
                }
              }

              return (
                <p key={resp.id}>
                  <strong>{title}</strong>
                  <br /> {answerDisplay}
                </p>
              );
            })}
          </Panel>
        );
      })}
    </div>
  );
};

export default ImportDataViewer;
